import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useAuthStore } from '../../auth/store';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { LoaderSymbol } from '../../components/loading';
import { useFetchMs } from '../../hooks/useFetchMs';
import { navMeta } from '../../layouts/navData';
import { DashboardDesignerFrame, type DashboardFrameMode } from './DashboardDesignerFrame';
import { dashboardFrameSrc } from './dashboardDesignerUrl';

// ─────────────────────────────────────────────────────────────────────────────
// DashboardViewerPage — To Do List → Sample Backlog / ICM (ADR-0016, option B).
// Each backlog entry opens ITS dbo.DashboardData dashboard in the SAME DevExpress
// v20.1 Web Dashboard the legacy app uses, in VIEW-ONLY mode:
//   {api}/reports/dashboard/viewer?id={oid}&rds={viewer session}
// The session (POST /api/v1/dashboards/{id}/viewer-session) is bound to this one
// dashboard and is open to any user it is assigned to — the same rule that shows
// the menu entry.  The forwarder lets it reach only this dashboard's viewer and
// data: no save, no designer, no data-source wizard, no other dashboard.
//
// Toolbar — the legacy dashboard view's own actions (ALPACA parity):
//   · Show Dashboard Designer (Alt+L) — opens THIS dashboard in the designer, in
//     place (the shared DashboardDesignerFrame, Back returns here and reloads).
//     Admin / service only, as in System → Dashboard; the server enforces it too
//     (the designer session is admin-or-service).
//   · Show Dashboard in a separate tab (Alt+T) — the same view-only viewer, full
//     window, in a new browser tab.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  /** The nav id (bl-due, icm01, …) — drives the title/icon via navMeta. */
  pageId: string;
  /** dbo.DashboardData.Oid to show. */
  dashboardId: string;
}

interface ViewerSession { token: string; idleSeconds: number; }

function apiErrMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
    ?? (e instanceof Error ? e.message : fallback);
}

export function DashboardViewerPage({ pageId, dashboardId }: Props) {
  const [session, setSession] = useState<ViewerSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by Refresh (and on return from the designer): a fresh session + a fresh
  // frame, so the dashboard re-reads its layout and re-queries its data.
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const loadMs = useFetchMs(loading);
  // Designer open in place (null = viewing).
  const [designMode, setDesignMode] = useState<DashboardFrameMode | null>(null);

  // Same gate as System → Dashboard (nav mode admin-or-service); the API enforces it.
  const canDesign = useAuthStore((s) =>
    (s.permissions?.isAdmin ?? false) || (s.user?.userName ?? '').trim().toLowerCase() === 'service');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSession(null);
    (async () => {
      try {
        const res = await api.post(`/api/v1/dashboards/${dashboardId}/viewer-session`);
        if (!cancelled) setSession((res.data as { data: ViewerSession }).data);
      } catch (e) {
        if (!cancelled) { setError(apiErrMsg(e, 'API error.')); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [dashboardId, reload]);

  const title = navMeta(pageId)?.label ?? 'Dashboard';
  const src = session ? dashboardFrameSrc('view', dashboardId, session.token) : null;

  const refresh = useCallback(() => setReload(n => n + 1), []);
  const openDesigner = useCallback(() => { if (canDesign) setDesignMode('design'); }, [canDesign]);
  const closeDesigner = useCallback(() => { setDesignMode(null); setReload(n => n + 1); }, []);
  // Synchronous window.open on the click (no await first) so the browser never treats
  // it as a pop-up.  The tab reuses this page's view-only session.
  const openInTab = useCallback(() => {
    if (src) window.open(src, '_blank', 'noopener');
  }, [src]);

  // Bespoke toolbar actions → keys declared next to the buttons (standing rule 14b);
  // inert while the designer (which registers its own keys) is open.
  useHotkeys([
    { combo: 'alt+l', label: 'Show Dashboard Designer', group: title, order: 1,
      disabled: !canDesign || designMode !== null, run: openDesigner },
    { combo: 'alt+t', label: 'Show Dashboard in a separate tab', group: title, order: 2,
      disabled: !src || designMode !== null, run: openInTab },
  ]);

  if (designMode) {
    return (
      <div className="bl-page">
        <DashboardDesignerFrame
          dashboardId={dashboardId}
          title={title}
          mode={designMode}
          onModeChange={setDesignMode}
          onBack={closeDesigner}
        />
      </div>
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-grid-area">
        <div className="section-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <FormTitle pageId={pageId} loadMs={loadMs} loading={loading} />
          <AppToolbar
            left={
              <>
                {canDesign && (
                  <button type="button" className="ca-btn" onClick={openDesigner}
                          title={`Open this dashboard in the Dashboard Designer (${formatCombo('alt+l')})`}>
                    <i className="ti ti-chart-pie" /> Show Dashboard Designer
                  </button>
                )}
                <button type="button" className="ca-btn" onClick={openInTab} disabled={!src}
                        title={`Open this dashboard in a new browser tab (${formatCombo('alt+t')})`}>
                  <i className="ti ti-external-link" /> Show Dashboard in a separate tab
                </button>
              </>
            }
            onRefresh={refresh}
            refreshing={loading}
          />
        </div>
        <div className="bl-card bl-card-flex dvp-card">
          {error ? (
            <div className="dvp-msg dvp-msg-err"><i className="ti ti-alert-triangle" /> {error}</div>
          ) : !src ? (
            <div className="dvp-msg"><LoaderSymbol size={28} /> Opening the dashboard…</div>
          ) : (
            <iframe key={src} title={title} src={src} className="dvp-iframe" onLoad={() => setLoading(false)} />
          )}
        </div>
      </div>
      <style>{dvpStyles}</style>
    </div>
  );
}

const dvpStyles = `
  .dvp-card { position:relative; min-height:420px; }
  .dvp-iframe { position:absolute; inset:0; width:100%; height:100%; border:0; display:block; background:#fff; }
  .dvp-msg { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; gap:10px;
    color:var(--ai-muted); font-size:13px; padding:24px; text-align:center; }
  .dvp-msg-err { color:var(--ai-red); }
`;
